import * as React from 'react';
import { Box, Stack, Typography, styled } from '@mui/material';
import moment, { Moment } from 'moment';
import { CalendarEventOutput } from '../calendar-types';
import { getEventColor } from './calendar-colors';

interface MonthViewProps {
  monthStart: Moment;
  events: CalendarEventOutput[];
  onEventClick: (event: CalendarEventOutput) => void;
  onSlotClick: (datetime: string) => void;
}


export const MonthView = ({ monthStart, events, onEventClick, onSlotClick }: MonthViewProps) => {
  const today = moment().startOf('day');

  const gridStart = monthStart.clone().startOf('month').startOf('week');
  const weeks: Moment[][] = [];
  const cur = gridStart.clone();
  for (let w = 0; w < 6; w++) {
    const week: Moment[] = [];
    for (let d = 0; d < 7; d++) {
      week.push(cur.clone());
      cur.add(1, 'day');
    }
    weeks.push(week);
  }

  const eventsByDate = React.useMemo(() => {
    const map = new Map<string, CalendarEventOutput[]>();
    for (const e of events) {
      if (!e.startDateTime) continue;
      const key = moment(e.startDateTime).format('YYYY-MM-DD');
      const existing = map.get(key);
      if (existing) existing.push(e);
      else map.set(key, [e]);
    }
    map.forEach((arr) => arr.sort((a, b) => moment(a.startDateTime).diff(moment(b.startDateTime))));
    return map;
  }, [events]);

  const getEventsForDay = (day: Moment) =>
    eventsByDate.get(day.format('YYYY-MM-DD')) ?? [];

  return (
    <Root>
      <WeekdayHeaderRow>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <WeekdayHeaderCell key={d}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {d}
            </Typography>
          </WeekdayHeaderCell>
        ))}
      </WeekdayHeaderRow>

      <WeekGridScroll>
        {weeks.map((week, wi) => (
          <WeekRow key={wi}>
            {week.map((day, di) => {
              const isToday = day.isSame(today, 'day');
              const isCurrentMonth = day.month() === monthStart.month();
              const dayEvents = getEventsForDay(day);

              return (
                <DayCell
                  key={di}
                  isCurrentMonth={isCurrentMonth}
                  hasLeftBorder={di > 0}
                  onClick={() => onSlotClick(day.clone().hours(9).toISOString())}
                >
                  <DateCircle isToday={isToday}>
                    <Typography
                      sx={{
                        color: isToday ? 'common.white' : isCurrentMonth ? 'text.primary' : 'text.disabled',
                        fontWeight: isToday ? 700 : 400,
                        fontSize: 13,
                        lineHeight: 1,
                      }}
                    >
                      {day.format('D')}
                    </Typography>
                  </DateCircle>

                  <Stack gap={0.25}>
                    {dayEvents.slice(0, 3).map((event) => {
                      const isCancelled = event.status === 'cancelled';
                      const eventColor = getEventColor(event.colorId, event.status);
                      return (
                        <EventChip
                          key={event.id}
                          onClick={(e) => { e.stopPropagation(); onEventClick(event); }}
                          style={{ borderLeft: `3px solid ${eventColor.border}`, backgroundColor: eventColor.bg }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: 10,
                              color: isCancelled ? 'text.disabled' : eventColor.text,
                              fontWeight: 500,
                              textDecoration: isCancelled ? 'line-through' : 'none',
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {moment(event.startDateTime).format('h:mm')} {event.summary || '(No title)'}
                          </Typography>
                        </EventChip>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary', pl: 0.5 }}>
                        +{dayEvents.length - 3} more
                      </Typography>
                    )}
                  </Stack>
                </DayCell>
              );
            })}
          </WeekRow>
        ))}
      </WeekGridScroll>
    </Root>
  );
};

interface WithIsToday {
  isToday?: boolean;
}

interface DayCellProps {
  isCurrentMonth?: boolean;
  hasLeftBorder?: boolean;
}

const Root = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden',
});

const WeekdayHeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const WeekdayHeaderCell = styled(Box)({
  flex: 1,
  textAlign: 'center',
  paddingTop: 6,
  paddingBottom: 6,
});

const WeekGridScroll = styled(Box)({
  flex: 1,
  overflow: 'auto',
});

const WeekRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  minHeight: 110,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const DayCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isCurrentMonth' && prop !== 'hasLeftBorder',
})<DayCellProps>(({ theme, isCurrentMonth, hasLeftBorder }) => ({
  flex: 1,
  borderLeft: hasLeftBorder ? `1px solid ${theme.palette.divider}` : 'none',
  padding: 4,
  cursor: 'pointer',
  backgroundColor: isCurrentMonth ? theme.palette.background.paper : theme.palette.action.disabledBackground,
  '&:hover': { backgroundColor: isCurrentMonth ? theme.palette.action.hover : undefined },
  transition: 'background-color 0.15s',
  overflow: 'hidden',
}));

const DateCircle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isToday',
})<WithIsToday>(({ theme, isToday }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  borderRadius: '50%',
  marginBottom: 2,
  backgroundColor: isToday ? theme.palette.primary.main : 'transparent',
}));

const EventChip = styled(Box)({
  paddingLeft: 4,
  paddingTop: 1,
  paddingBottom: 1,
  borderRadius: '0 2px 2px 0',
  cursor: 'pointer',
  '&:hover': { filter: 'brightness(0.94)' },
});
